import React, { useState, useEffect } from "react";
import { X, ZoomIn } from "lucide-react";
import api from "@/lib/api";

const categories = ["Semua", "Belajar", "Kegiatan", "Fasilitas"];

export default function GaleriFoto() {
  const [activeCategory, setActiveCategory] = useState("Semua");
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/gallery")
      .then((res) => setPhotos(res.data))
      .catch(() => setPhotos([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = activeCategory === "Semua" ? photos : photos.filter((p) => p.category === activeCategory);

  return (
    <div data-testid="galeri-foto-page" style={{ paddingTop: "60px" }}>
      {/* Header - lebih ringkas di mobile */}
      <div
        className="py-8 sm:py-14 text-white text-center relative overflow-hidden"
        style={{ backgroundColor: "#198530" }}
      >
        <div className="absolute inset-0" style={{ opacity: 0.05 }}>
          <svg width="100%" height="100%">
            <defs>
              <pattern id="dots2" x="0" y="0" width="30" height="30" patternUnits="userSpaceOnUse">
                <circle cx="15" cy="15" r="2" fill="white" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#dots2)" />
          </svg>
        </div>
        <div className="relative z-10 px-4">
          <p className="text-xs sm:text-sm uppercase tracking-widest mb-1" style={{ color: "#d4af37" }}>Dokumentasi</p>
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold">Galeri Foto</h1>
          <p className="mt-2 text-xs sm:text-sm md:text-base" style={{ color: "#bbf7d0" }}>
            Dokumentasi kegiatan belajar dan fasilitas Yayasan Tozan
          </p>
        </div>
      </div>

      <section className="py-8 sm:py-12" style={{ backgroundColor: "#F8FAF8" }}>
        <div className="max-w-6xl mx-auto px-3 sm:px-4">
          {/* Category Filter */}
          <div className="flex justify-center gap-2 flex-wrap mb-6 sm:mb-10">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                data-testid={`gallery-filter-${cat}`}
                className="px-4 py-1.5 rounded-full font-semibold text-xs sm:text-sm transition-all duration-200"
                style={{
                  backgroundColor: activeCategory === cat ? "#198530" : "white",
                  color: activeCategory === cat ? "white" : "#374151",
                  border: activeCategory === cat ? "2px solid #198530" : "2px solid #e5e7eb",
                }}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Photo Grid - 2 kolom di mobile */}
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
            {filtered.map((photo, idx) => (
              <div
                key={photo.id || idx}
                data-testid={`gallery-photo-${idx}`}
                className="group cursor-pointer"
                onClick={() => setSelectedPhoto(photo)}
              >
                {/* Foto */}
                <div
                  className="relative rounded-lg sm:rounded-xl overflow-hidden"
                  style={{ aspectRatio: "4/3" }}
                >
                  <img
                    src={photo.image}
                    alt={photo.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                  />
                  {/* Hover overlay zoom icon (desktop) */}
                  <div
                    className="absolute inset-0 hidden sm:flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300"
                    style={{ background: "rgba(0,0,0,0.35)" }}
                  >
                    <ZoomIn size={28} className="text-white" />
                  </div>
                  {/* Mobile: tap overlay */}
                  <div className="absolute inset-0 sm:hidden flex items-end" style={{ background: "linear-gradient(transparent 50%, rgba(0,0,0,0.4) 100%)" }} />
                </div>
                {/* Keterangan foto - selalu tampil di bawah */}
                <div className="pt-1.5 pb-0.5 px-0.5">
                  <p className="font-semibold text-gray-800 leading-snug line-clamp-1" style={{ fontSize: "0.72rem" }}>
                    {photo.title}
                  </p>
                  <p className="text-xs" style={{ color: "#198530", fontSize: "0.65rem" }}>{photo.category}</p>
                </div>
              </div>
            ))}
          </div>

          {!loading && filtered.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <p>Tidak ada foto dalam kategori ini.</p>
            </div>
          )}
          {loading && <div className="text-center py-12 text-gray-400">Memuat galeri...</div>}
        </div>
      </section>

      {/* Lightbox */}
      {selectedPhoto && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.92)" }}
          onClick={() => setSelectedPhoto(null)}
          data-testid="lightbox"
        >
          <button
            className="absolute top-3 right-3 text-white p-2 rounded-full transition-colors hover:bg-white/20"
            onClick={() => setSelectedPhoto(null)}
            data-testid="lightbox-close"
          >
            <X size={28} />
          </button>
          <div
            className="max-w-4xl max-h-[90vh] w-full text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedPhoto.image}
              alt={selectedPhoto.title}
              className="max-w-full max-h-[75vh] rounded-xl object-contain shadow-2xl mx-auto"
            />
            <p className="text-white text-base sm:text-lg font-semibold mt-3">{selectedPhoto.title}</p>
            <p className="text-xs sm:text-sm mt-1" style={{ color: "#d4af37" }}>{selectedPhoto.category}</p>
          </div>
        </div>
      )}
    </div>
  );
}
